import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Query keys factory for consistent key structure across the application.
 * Hierarchical keys enable granular cache invalidation.
 */
export const queryKeys = {
  projects: {
    all: ['/api/projects'] as const,
    detail: (id: string) => ['/api/projects', id] as const,
    budgetSummary: (id: string) => ['/api/projects', id, 'budget-summary'] as const,
    scripts: (id: string) => ['/api/projects', id, 'scripts'] as const,
    assignments: (id: string) => ['/api/projects', id, 'assignments'] as const,
    approvalWorkflows: (id: string) => ['/api/projects', id, 'approval-workflows'] as const,
    approvalWorkflowsWithSteps: (id: string) => ['/api/projects', id, 'approval-workflows-with-steps'] as const,
    deliverablesWithRevisions: (id: string) => ['/api/projects', id, 'deliverables-with-revisions'] as const,
    revisionsWithDetails: (id: string) => ['/api/projects', id, 'revisions-with-details'] as const,
  },
  creators: {
    all: ['/api/creators'] as const,
    detail: (id: string) => ['/api/creators', id] as const,
  },
  clients: {
    all: ['/api/clients'] as const,
    detail: (id: string) => ['/api/clients', id] as const,
  },
  scripts: {
    all: ['/api/scripts'] as const,
    detail: (id: string) => ['/api/scripts', id] as const,
    assignments: (id: string) => ['/api/scripts', id, 'assignments'] as const,
  },
  notifications: {
    all: ['/api/notifications'] as const,
    unreadCount: ['/api/notifications', 'unread-count'] as const,
  },
  stats: {
    all: ['/api/stats'] as const,
  },
  approvalWorkflows: {
    detail: (id: string) => ['/api/approval-workflows', id] as const,
    steps: (id: string) => ['/api/approval-workflows', id, 'steps'] as const,
  },
  revisions: {
    all: ['/api/revisions'] as const,
  },
  creativeStages: {
    // NOTE: These queryKeys use custom queryFn because they don't match the default join("/") pattern
    // Actual endpoint: /api/projects/${projectId}/creative-stages
    all: (projectId: string) => ['/api/creative-stages', projectId] as const,
    // Actual endpoint: /api/creative-stages/${stageId}
    detail: (stageId: string) => ['/api/creative-stages', stageId] as const,
    // Actual endpoint: /api/creative-stages/${stageId}/criteria
    criteria: (stageId: string) => ['/api/stage-criteria', stageId] as const,
    dashboard: (projectId: string, dateRange?: string) => 
      dateRange 
        ? ['/api/creative-dashboard', projectId, dateRange] as const
        : ['/api/creative-dashboard', projectId] as const,
    comparison: (stageIds: string[]) => ['/api/stage-details', stageIds.join(',')] as const,
  },
} as const;

export async function apiRequest(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const res = await fetch(url, {
    ...options,
    credentials: "include",
  });
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000, // 5 minutes - reasonable default for mostly static data
      gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors except 429 (rate limiting)
        if (error && typeof error === 'object' && 'message' in error) {
          const errorMessage = String(error.message);
          const statusMatch = errorMessage.match(/^(\d{3}):/);
          if (statusMatch) {
            const status = parseInt(statusMatch[1], 10);
            if (status >= 400 && status < 500 && status !== 429) {
              return false;
            }
          }
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

/**
 * Granular cache invalidation helpers.
 * Invalidate only what changed to minimize unnecessary refetches.
 */

/**
 * Invalidate a specific project's data (including nested resources).
 * Does NOT invalidate the project list - use invalidateProjectsList() for that.
 */
export const invalidateProjectData = (projectId: string) => {
  if (!projectId) return;
  queryClient.invalidateQueries({ 
    predicate: q => Array.isArray(q.queryKey) && 
                   q.queryKey[0] === "/api/projects" && 
                   q.queryKey[1] === projectId
  });
};

/**
 * Invalidate the list of all projects (not individual project details).
 * Use when creating or deleting projects.
 */
export const invalidateProjectsList = () => {
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.projects.all,
    exact: true
  });
};

/**
 * Invalidate both a specific project AND the projects list.
 * Use when a project change affects the list (e.g., status changes that affect filters).
 */
export const invalidateProjectAndList = (projectId: string) => {
  invalidateProjectData(projectId);
  invalidateProjectsList();
};

/**
 * Invalidate a specific script's data including its assignments.
 */
export const invalidateScriptData = (scriptId: string) => {
  if (!scriptId) return;
  queryClient.invalidateQueries({
    predicate: q => Array.isArray(q.queryKey) && 
                   q.queryKey[0] === "/api/scripts" && 
                   q.queryKey[1] === scriptId
  });
};

/**
 * Invalidate script assignments specifically.
 * Use after creating/updating/deleting assignments.
 */
export const invalidateScriptAssignments = (scriptId: string) => {
  if (!scriptId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.scripts.assignments(scriptId) });
};

/**
 * Invalidate project assignments specifically.
 * Use after WhatsApp messages or other status changes.
 */
export const invalidateProjectAssignments = (projectId: string) => {
  if (!projectId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.projects.assignments(projectId) });
};

/**
 * Invalidate scripts for a specific project.
 */
export const invalidateProjectScripts = (projectId: string) => {
  if (!projectId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.projects.scripts(projectId) });
};

/**
 * Invalidate all notifications.
 */
export const invalidateNotifications = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
};

/**
 * Invalidate all creators data.
 */
export const invalidateCreators = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.creators.all });
};

/**
 * Invalidate a specific creator's data.
 */
export const invalidateCreatorData = (creatorId: string) => {
  if (!creatorId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.creators.detail(creatorId) });
};

/**
 * Invalidate all clients data.
 */
export const invalidateClients = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
};
